import { cssMerge } from '@volvo-cars/css/utils';
import type React from 'react';
import { forwardRef } from 'react';
import { ErrorMessage } from './error-message';
import { Hint } from './hint';
import type { BaseInputProps, GlobalHTMLAttributes } from './types';
import { useIds } from './use-ids';

export interface TextAreaProps
  extends GlobalHTMLAttributes, BaseInputProps<HTMLTextAreaElement> {
  /**
   * @default 'top-label'
   */
  variant?: 'top-label' | 'floating-label';

  /**
   * The name of the textarea to use when submitting the form.
   */
  name: string;

  /**
   * Placeholder text to show in the textarea when it is empty.
   */
  placeholder?: string;

  /**
   * Minimum length of the textarea value.
   */
  minLength?: number;

  /**
   * Maximum length of the textarea value.
   */
  maxLength?: number;

  /**
   * The number of visible text lines to show in the textarea.
   *
   * @default 2
   */
  rows?: number;

  /**
   * Controls whether and how text input is automatically capitalized as it is entered by the user.
   */
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';

  /**
   * Controls whether the element may be checked for spelling errors.
   */
  spellCheck?: boolean;

  /**
   * Enable or disable autocomplete in the textarea.
   */
  autoComplete?: 'off' | 'on';

  /**
   * Set the error message of a textarea and mark it invalid.
   */
  errorMessage?: string;

  /**
   * Force the textarea to be invalid.
   *
   * @default false
   */
  'aria-invalid'?: boolean;

  /**
   * Default value of an uncontrolled textarea.
   */
  defaultValue?: string;

  /**
   * Value of the textarea.
   *
   * Makes the textarea controlled.
   */
  value?: string;

  /**
   * Fires immediately when the textarea's value is changed by the user (for example, it fires on every keystroke).
   */
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
}

/**
 * TextArea should be used when the information is known by the user.
 * It can be used by itself or in combination with any other inputs.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    {
      variant = 'top-label',
      hint,
      id,
      label,
      hidden,
      dir,
      errorMessage: errorMessageProp,
      lang,
      translate,
      slot,
      className,
      style,
      ...props
    }: TextAreaProps,
    ref: React.ForwardedRef<HTMLTextAreaElement>,
  ) {
    const { inputId, errorId, hintId } = useIds(id);
    // Hide error message and error state when input is disabled
    const errorMessage = !props.disabled ? errorMessageProp : undefined;
    const ariaInvalid = props['aria-invalid'] || !!errorMessage;

    return (
      <div
        id={id}
        className={cssMerge(
          variant === 'floating-label'
            ? 'input-floating-label'
            : 'flex-col items-start gap-8',
          className,
        )}
        hidden={hidden}
        dir={dir}
        lang={lang}
        translate={translate}
        slot={slot}
        style={style}
      >
        <label htmlFor={inputId}>{label}</label>
        <textarea
          // Needed to enable the :placeholder-shown pseudo-class for CSS
          // floating labels.
          placeholder={variant === 'floating-label' ? ' ' : undefined}
          {...props}
          ref={ref}
          id={inputId}
          aria-invalid={ariaInvalid ? true : undefined}
          aria-errormessage={errorMessage ? errorId : undefined}
          aria-describedby={[errorMessage && errorId, hint && hintId]
            .filter(Boolean)
            .join(' ')}
        />
        <ErrorMessage
          errorMessage={errorMessage}
          id={errorId}
          className={variant === 'floating-label' ? 'mt-8' : undefined}
        />
        {hint && (
          <Hint
            id={hintId}
            className={variant === 'floating-label' ? 'mt-8' : undefined}
          >
            {hint}
          </Hint>
        )}
      </div>
    );
  },
);
